import React, { useCallback, useMemo, useState } from "react";
import { getWizardElement } from "../WizardElements";
import { Button } from "@/components/ui/button";
import IconProvider from "@/components/ui/IconProvider";
import { DocumentationTooltip } from "@/components/ui/DocumentationTooltip";
import { FormFieldObject } from "@/lib/stores/objectsStore";
import { useNavigate } from "react-router-dom";

import {
  isTextLikeField,
  getFormattedFieldValue,
  getTypeElementInfo,
  getGlossaryItemInfo,
  getDialogInfo,
  shouldTreatAsOptional,
} from "./SectionFormFunctions/objectFormUtils";
import { FormMode } from "./SectionFormFunctions/objectFormTypes";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import {
  BlueprintVersionChanges,
  fieldHasChanges,
} from "@/lib/utils/blueprintVersionUtils";
import { getCachedStringsItems } from "@/lib/stores/stringsStore";
import { useDisplayLanguageOptional } from "@/contexts/DisplayLanguageContext";
import {
  SourceContextPanel,
  SourceTextSection,
} from "@/components/wizards/shared/source-context-panel";

// ============================================================================
// TRANSLATABLE FIELD WRAPPER
// ============================================================================

/**
 * Wraps a field element with a floating SourceContextPanel that shows
 * the source text when the field is focused and a non-source language is selected.
 */
const TranslatableFieldWrapper: React.FC<{
  field: FormFieldObject;
  gameId?: string;
  children: React.ReactNode;
}> = ({ field, gameId, children }) => {
  const [isFocused, setIsFocused] = useState(false);
  const displayLanguageCtx = useDisplayLanguageOptional();
  const displayLanguage = displayLanguageCtx?.displayLanguage ?? null;

  const isTranslationMode = displayLanguage !== null && !!field.string_id;

  // Get source text from TMS string
  const sourceText = useMemo(() => {
    if (!field.string_id || !gameId) return '';
    const strings = getCachedStringsItems(gameId);
    const stringItem = strings.find(s => s.id === field.string_id);
    return stringItem?.source_text || '';
  }, [field.string_id, gameId]);

  // Get the loca key (abbreviation) from the TMS string
  const locaKey = useMemo(() => {
    if (!field.string_id || !gameId) return null;
    const strings = getCachedStringsItems(gameId);
    const stringItem = strings.find(s => s.id === field.string_id);
    return stringItem?.abbrev || null;
  }, [field.string_id, gameId]);

  // If not in translation mode, just render children without the wrapper overhead
  if (!isTranslationMode) {
    return <>{children}</>;
  }

  return (
    <div
      onFocus={() => setIsFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setIsFocused(false);
        }
      }}
    >
      {children}
      <SourceContextPanel isVisible={isFocused && !!sourceText}>
        <SourceTextSection sourceText={sourceText} locaKey={locaKey} />
      </SourceContextPanel>
    </div>
  );
};

interface FieldRendererProps {
  field: FormFieldObject;
  mode: FormMode;
  canEdit?: boolean; // Whether user can edit (controls field locking)
  fieldValue: any;
  onLocalInputChange: (fieldId: string, value: any) => void;
  onFieldBlur: (fieldId: string, value: any) => void;
  onMultipleFieldChange: (fieldId: string, index: number, value: any) => void;
  onAddFieldInstance: (fieldId: string) => void;
  onRemoveFieldInstance: (fieldId: string, index: number) => void;
  onMoveFieldInstance?: (fieldId: string, index: number, direction: 'up' | 'down') => void;
  onReorderFieldInstances?: (fieldId: string, fromIndex: number, toIndex: number) => void;
  getCurrentFieldContent: (field: FormFieldObject) => string;
  types: any[];
  glossaryItems: any[];
  dialogs?: any[];
  getStringContent: (field: FormFieldObject) => string | null;
  blueprintChanges?: BlueprintVersionChanges | null;
  gameId?: string;
  showDocumentation?: boolean;
}

/**
 * Component for rendering individual form fields
 */
export const GlossaryFieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  mode,
  canEdit = true, // Default to true for backwards compatibility
  fieldValue,
  onLocalInputChange,
  onFieldBlur,
  onMultipleFieldChange,
  onAddFieldInstance,
  onRemoveFieldInstance,
  onMoveFieldInstance,
  onReorderFieldInstances,
  getCurrentFieldContent,
  types,
  glossaryItems,
  dialogs = [],
  getStringContent,
  blueprintChanges,
  gameId,
  showDocumentation = false,
}) => {
  const { t } = usePlatformTranslation();
  const navigate = useNavigate();
  const isViewMode = mode === "view";
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Check if field is readonly (can be set by role visibility rules or user permissions)
  const isFieldReadOnly = field.isReadOnly || !canEdit;
  // State to track if multiple items are expanded
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const renderFieldValueForView = useCallback(
    (field: FormFieldObject, fieldValue: any): React.ReactNode => {
      if (
        fieldValue === undefined ||
        fieldValue === null ||
        fieldValue === ""
      ) {
        return <span className="text-muted-foreground italic text-sm">{t('platform.wizards.glossary.not_set')}</span>;
      }

      // Helper function to wrap content with translation icon if needed
      // Note: This is now only used for non-wizard elements (like URLs, types, etc.)
      // Wizard elements should not have the globe icon wrapped around them
      const wrapWithTranslationIcon = (content: React.ReactNode) => {
        if (field.translation) {
          return (
            <div className="flex items-center gap-2">
              <IconProvider iconstring="fa-globe" size={16} className="text-muted-foreground" />
              {content}
            </div>
          );
        }
        return content;
      };

      // Check if this is a translatable field or text-like field that should use TextAreaElement
      if (field.translation || isTextLikeField(field.type)) {
        // For translatable fields, get the content from the strings store if available
        let contentToRender = fieldValue;
        if (field.translation && field.string_id) {
          const stringContent = getStringContent(field);
          if (stringContent) {
            contentToRender = stringContent;
          }
        }
        // Use TextAreaElement for both translatable fields and text-like fields
        // This ensures consistent rendering with proper formatting support
        const wizardElement = getWizardElement(
          { ...field, type: "textarea" }, // Force textarea type to use TextAreaElement
          contentToRender,
          () => {}, // No-op onChange for view mode
          () => {}, // No-op onBlur for view mode
          gameId
        );

        // Clone the element and add disabled and viewMode props
        // TextAreaElement supports disabled and viewMode props for proper view rendering
        const viewModeElement = React.cloneElement(wizardElement, {
          disabled: true,
          viewMode: "view-formatted" as const,
        });

        // Return wizard element without wrapping it with translation icon
        // The globe icon should only appear in the field label, not around the element
        return viewModeElement;
      }

      switch (field.type) {
        case "url":
          if (fieldValue && typeof fieldValue === "object" && fieldValue.url) {
            return wrapWithTranslationIcon(
              <a
                href={fieldValue.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 underline cursor-pointer"
              >
                {fieldValue.text || fieldValue.url}
              </a>
            );
          }
          return wrapWithTranslationIcon(
            <div className="text-foreground">{fieldValue || ""}</div>
          );

        case "types": {
          // Handle both array format (legacy) and comma-separated string format (current)
          let selectedTypes: string[] = [];
          if (Array.isArray(fieldValue)) {
            selectedTypes = fieldValue;
          } else if (typeof fieldValue === "string" && fieldValue.trim()) {
            selectedTypes = fieldValue
              .split(",")
              .map((id) => id.trim())
              .filter((id) => id);
          } else if (fieldValue) {
            selectedTypes = [String(fieldValue)];
          }

          if (selectedTypes.length === 0) {
            return (
              <span className="text-muted-foreground italic text-sm">
                No types selected
              </span>
            );
          }

          // Show collapsed view if more than 1 item and not expanded
          const displayTypes = isExpanded || selectedTypes.length === 1 
            ? selectedTypes 
            : selectedTypes.slice(0, 1);
          const hasMore = selectedTypes.length > 1;

          return (
            <div className="space-y-1.5">
              {displayTypes.map((typeId: string, index: number) => {
                const typeInfo = getTypeElementInfo(typeId, types);
                return (
                  <div key={index} className=" ">
                    <div className="font-medium text-sm text-foreground">
                      {typeInfo.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {typeInfo.description}
                    </div>
                  </div>
                );
              })}
              {hasMore && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-sm text-primary hover:text-primary/80 font-medium cursor-pointer hover:underline"
                >
                  {isExpanded 
                    ? "Show less" 
                    : `+${selectedTypes.length - 1} more`}
                </button>
              )}
            </div>
          );
        }

        case "glossaryItem": {
          // Handle both array format (legacy) and comma-separated string format (current)
          let selectedItems: string[] = [];
          if (Array.isArray(fieldValue)) {
            selectedItems = fieldValue;
          } else if (typeof fieldValue === "string" && fieldValue.trim()) {
            selectedItems = fieldValue
              .split(",")
              .map((id) => id.trim())
              .filter((id) => id);
          } else if (fieldValue) {
            selectedItems = [String(fieldValue)];
          }

          // Filter out items that don't exist in the glossary
          const validItems = selectedItems.filter((itemId) => {
            const itemInfo = getGlossaryItemInfo(itemId, glossaryItems);
            return !itemInfo.name.startsWith("Unknown Item");
          });

          if (validItems.length === 0) {
            return (
              <span className="text-muted-foreground italic text-sm">
                No items selected
              </span>
            );
          }

          // Show collapsed view if more than 1 item and not expanded
          const displayItems = isExpanded || validItems.length === 1 
            ? validItems 
            : validItems.slice(0, 1);
          const hasMoreItems = validItems.length > 1;

          return (
            <div className="space-y-1.5">
              {displayItems.map((itemId: string, index: number) => {
                const itemInfo = getGlossaryItemInfo(itemId, glossaryItems);
                return (
                  <div
                    key={index}
                    className="  cursor-pointer transition-colors flex items-center justify-between group"
                    onClick={() => {
                      // Navigate to the glossary item page in a new tab
                      navigate(`/library/${itemId}`);
                    }}
                  >
                    <div className="font-medium text-sm text-foreground">
                      {itemInfo.name}
                    </div>
                    <IconProvider
                      iconstring="fa-chevron-right"
                      size={12}
                      className="text-muted-foreground group-hover:text-foreground transition-colors"
                    />
                  </div>
                );
              })}
              {hasMoreItems && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-sm text-primary hover:text-primary/80 font-medium cursor-pointer hover:underline"
                >
                  {isExpanded 
                    ? "Show less" 
                    : `+${validItems.length - 1} more`}
                </button>
              )}
            </div>
          );
        }

        case "dialog": {
          // Handle both array format (legacy) and comma-separated string format (current)
          let selectedDialogs: string[] = [];
          if (Array.isArray(fieldValue)) {
            selectedDialogs = fieldValue;
          } else if (typeof fieldValue === "string" && fieldValue.trim()) {
            selectedDialogs = fieldValue
              .split(",")
              .map((id) => id.trim())
              .filter((id) => id);
          } else if (fieldValue) {
            selectedDialogs = [String(fieldValue)];
          }

          // Filter out dialogs that don't exist
          const validDialogs = selectedDialogs.filter((dialogId) => {
            const dialogInfo = getDialogInfo(dialogId, dialogs);
            return !dialogInfo.name.startsWith("Unknown Dialog");
          });

          if (validDialogs.length === 0) {
            return (
              <span className="text-muted-foreground italic text-sm">
                No dialogs selected
              </span>
            );
          }

          // Show collapsed view if more than 1 dialog and not expanded
          const displayDialogs = isExpanded || validDialogs.length === 1 
            ? validDialogs 
            : validDialogs.slice(0, 1);
          const hasMoreDialogs = validDialogs.length > 1;

          return (
            <div className="space-y-1.5">
              {displayDialogs.map((dialogId: string, index: number) => {
                const dialogInfo = getDialogInfo(dialogId, dialogs);
                return (
                  <div
                    key={index}
                    className="cursor-pointer transition-colors flex items-center justify-between group"
                    onClick={() => {
                      // Navigate to the dialog page in a new tab
                      navigate(`/dialogs/${dialogId}`);
                    }}
                  >
                    <div className="flex flex-col">
                      <div className="font-medium text-sm text-foreground">
                        {dialogInfo.name}
                      </div>
                      {dialogInfo.dialog_key && (
                        <div className="text-xs text-muted-foreground">
                          Key: {dialogInfo.dialog_key}
                        </div>
                      )}
                    </div>
                    <IconProvider
                      iconstring="fa-chevron-right"
                      size={12}
                      className="text-muted-foreground group-hover:text-foreground transition-colors"
                    />
                  </div>
                );
              })}
              {hasMoreDialogs && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-sm text-primary hover:text-primary/80 font-medium cursor-pointer hover:underline"
                >
                  {isExpanded 
                    ? "Show less" 
                    : `+${validDialogs.length - 1} more`}
                </button>
              )}
            </div>
          );
        }

        case "image": {
          // Use the TabbedFileUploaderComponent with disabled prop for consistent rendering
          const wizardElement = getWizardElement(
            field,
            fieldValue,
            () => {}, // No-op onChange for view mode
            () => {}, // No-op onBlur for view mode
            gameId
          );

          // Clone the element and add disabled prop
          const viewModeElement = React.cloneElement(wizardElement, {
            disabled: true,
          });

          return viewModeElement;
        }

        case "file": {
          // Use the TabbedFileUploaderComponent with disabled prop for consistent rendering
          const wizardElement = getWizardElement(
            field,
            fieldValue,
            () => {}, // No-op onChange for view mode
            () => {}, // No-op onBlur for view mode
            gameId
          );

          // Clone the element and add disabled prop
          const viewModeElement = React.cloneElement(wizardElement, {
            disabled: true,
          });

          return viewModeElement;
        }

        case "number":
          return wrapWithTranslationIcon(
            <span className="text-foreground">{fieldValue}</span>
          );

        case "boolean": {
          const boolValue = typeof fieldValue === "boolean" ? fieldValue : fieldValue === "true" || fieldValue === true;
          return wrapWithTranslationIcon(
            <span className="text-foreground">
              {boolValue ? "True" : "False"}
            </span>
          );
        }

        default: {
          // For other field types, use the same wizard element approach
          const wizardElement = getWizardElement(
            field,
            fieldValue,
            () => {}, // No-op onChange for view mode
            () => {}, // No-op onBlur for view mode
            gameId
          );

          // Clone the element and add disabled and viewMode props
          const viewModeElement = React.cloneElement(wizardElement, {
            disabled: true,
            viewMode: "view-formatted" as const,
          });

          // Return wizard element without wrapping it with translation icon
          // The globe icon should only appear in the field label, not around the element
          return viewModeElement;
        }
      }
    },
    [getStringContent, types, glossaryItems, dialogs, navigate, isExpanded, gameId]
  );

  /**
   * Render field label
   */
  const renderFieldLabel = (field: FormFieldObject) => {
    const hasFieldChanges =
      blueprintChanges && fieldHasChanges(field.id, blueprintChanges);
    const isNewField = blueprintChanges?.addedFields.some(
      (f) => f.fieldId === field.id
    );
    const isModifiedField = blueprintChanges?.modifiedFields.some(
      (f) => f.fieldId === field.id
    );

    // Check if this required field should be treated as optional due to no available options
    const treatAsOptional = shouldTreatAsOptional(field, glossaryItems, types);

    return (
      <label
        className={`block text-sm font-medium ${
          hasFieldChanges
            ? isNewField
              ? "text-green-600 dark:text-green-400" // New fields in green
              : "text-red-600 dark:text-red-400" // Modified fields in red
            : "text-foreground" // Always use clean view mode styling
        }`}
      >
        <DocumentationTooltip documentation={field.documentation} disabled={isFieldReadOnly}>
          <div className="flex items-center gap-2">
            {field.translation && (
              <IconProvider iconstring="fa-globe" size={14} className="text-muted-foreground" />
            )}
            <span>
              {field.label}{" "}
              {field.required && !treatAsOptional && (
                <span
                  className={
                    hasFieldChanges
                      ? isNewField
                        ? "text-green-600 dark:text-green-400"
                        : "text-red-600 dark:text-red-400"
                      : "text-muted-foreground" // Always use clean view mode styling
                  }
                >
                  *
                </span>
              )}
            </span>
            {treatAsOptional && (
              <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 font-normal">
                No options available
              </span>
            )}
            {hasFieldChanges && (
              <span className="ml-1 text-xs px-1.5 py-0.5 rounded font-normal">
                {isNewField ? (
                  <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">
                    New
                  </span>
                ) : isModifiedField ? (
                  <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded">
                    Modified
                  </span>
                ) : null}
              </span>
            )}
          </div>
        </DocumentationTooltip>
      </label>
    );
  };

  /**
   * Render multiple field instances
   */
  const renderMultipleField = () => {
    const fieldValues = Array.isArray(fieldValue)
      ? fieldValue
      : [fieldValue].filter((v) => v !== undefined);
    const values = fieldValues.length === 0 ? [""] : fieldValues;
    const hasMultipleValues = values.length > 1;
    
    // Helper function to extract display value from potentially TranslatableValue objects
    const getDisplayValue = (value: any): string => {
      if (value && typeof value === 'object' && 'text' in value) {
        // TranslatableValue format
        return value.text || '';
      }
      return value || '';
    };

    // Drag and drop handlers
    const handleDragStart = (e: React.DragEvent, index: number) => {
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/html", e.currentTarget.innerHTML);
    };

    const handleDragEnd = () => {
      setDraggedIndex(null);
      setDragOverIndex(null);
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    };

    const handleDragEnter = (index: number) => {
      if (draggedIndex !== null && draggedIndex !== index) {
        setDragOverIndex(index);
      }
    };

    const handleDragLeave = () => {
      setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      if (draggedIndex !== null && draggedIndex !== dropIndex && onReorderFieldInstances) {
        // Adjust dropIndex because when we remove the dragged item, indices shift
        // If dragging forward (draggedIndex < dropIndex), the actual insert position is dropIndex - 1
        const adjustedDropIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
        onReorderFieldInstances(field.id, draggedIndex, adjustedDropIndex);
      }
      setDraggedIndex(null);
      setDragOverIndex(null);
    };

    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          {renderFieldLabel(field)}
          {hasMultipleValues && (
            <button
              type="button"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <span className="text-xs">({values.length} items)</span>
              <IconProvider
                iconstring={isCollapsed ? "fa-chevron-down" : "fa-chevron-up"}
                size={14}
                color="currentColor"
              />
            </button>
          )}
        </div>

        {/* Field Documentation Display - When documentation mode is on */}
        {showDocumentation && (
          <div className={`mb-2 p-2 rounded text-xs ${field.documentation ? 'bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-400 dark:border-blue-600' : 'bg-muted border-l-4 border-border'}`}>
            {field.documentation ? (
              <div 
                className="text-foreground prose prose-xs max-w-none"
                dangerouslySetInnerHTML={{ __html: field.documentation }}
              />
            ) : (
              <div className="text-muted-foreground italic">
                No documentation available
              </div>
            )}
          </div>
        )}

        {!isCollapsed &&
          values.map((value: any, index: number) => {
            // Don't show drop zone if it would result in the same position
            // (dropping before current position or immediately after)
            const shouldShowDropZone = draggedIndex !== null && 
                                      draggedIndex !== index && 
                                      draggedIndex !== index - 1;
            
            // Extract display value for translatable fields
            const displayValue = field.translation ? getDisplayValue(value) : value;
            
            return (
            <div key={index}>
              {/* Drop zone before the item */}
              {!isFieldReadOnly && field.multiple && values.length > 1 && onReorderFieldInstances && shouldShowDropZone && (
                  <div
                    onDragOver={handleDragOver}
                    onDragEnter={() => handleDragEnter(index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    className={`h-8 -mb-2 flex items-center justify-center transition-all ${
                      dragOverIndex === index
                        ? 'bg-primary/10 border-2 border-dashed border-primary rounded mb-1'
                        : 'opacity-0'
                    }`}
                  >
                    {dragOverIndex === index && (
                      <span className="text-xs text-primary font-medium">Drop here</span>
                    )}
                  </div>
                )}

              <div
                draggable={(!isFieldReadOnly && field.multiple && values.length > 1 && onReorderFieldInstances) ? "true" : "false"}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-start gap-2 transition-opacity ${
                  draggedIndex === index ? 'opacity-40' : 'opacity-100'
                }`}
              >
                {/* Drag handle - Only show when field is editable with multiple values */}
                {!isFieldReadOnly && field.multiple && values.length > 1 && onReorderFieldInstances && (
                  <div className="flex items-center mt-1 cursor-grab active:cursor-grabbing">
                    <svg
                      className="w-4 h-4 text-muted-foreground hover:text-foreground"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M9 3h2v2H9V3zm0 4h2v2H9V7zm0 4h2v2H9v-2zm0 4h2v2H9v-2zm0 4h2v2H9v-2zM13 3h2v2h-2V3zm0 4h2v2h-2V7zm0 4h2v2h-2v-2zm0 4h2v2h-2v-2zm0 4h2v2h-2v-2z" />
                    </svg>
                  </div>
                )}

                <div className="flex-1">
                  {isFieldReadOnly ? (
                    <div
                      className="p-2 border border-border rounded-sm bg-muted opacity-70"
                      data-field-type={field.type}
                    >
                      {renderFieldValueForView(field, displayValue)}
                    </div>
                  ) : field.translation && field.string_id ? (
                    <TranslatableFieldWrapper field={field} gameId={gameId}>
                      {getWizardElement(
                        field,
                        getFormattedFieldValue(field, displayValue),
                        (newValue) =>
                          onMultipleFieldChange(field.id, index, newValue),
                        () => {}, // No-op blur - changes are committed immediately via onMultipleFieldChange
                        gameId
                      )}
                    </TranslatableFieldWrapper>
                  ) : (
                    getWizardElement(
                      field,
                      getFormattedFieldValue(field, displayValue),
                      (newValue) =>
                        onMultipleFieldChange(field.id, index, newValue),
                      () => {}, // No-op blur - changes are committed immediately via onMultipleFieldChange
                      gameId
                    )
                  )}
                </div>

                {/* Only show remove button when field is editable */}
                {!isFieldReadOnly && field.multiple && values.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onRemoveFieldInstance(field.id, index)}
                    className="mt-1 p-1 text-muted-foreground hover:text-foreground transition-colors"
                    title={t('platform.wizards.glossary.remove_this_field')}
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* Drop zone after the last item */}
              {!isFieldReadOnly && field.multiple && values.length > 1 && onReorderFieldInstances && index === values.length - 1 && draggedIndex !== null && draggedIndex !== values.length - 1 && (
                <div
                  onDragOver={handleDragOver}
                  onDragEnter={() => handleDragEnter(values.length)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, values.length)}
                  className={`h-8 -mt-2 flex items-center justify-center transition-all ${
                    dragOverIndex === values.length
                      ? 'bg-primary/10 border-2 border-dashed border-primary rounded mt-1'
                      : 'opacity-0'
                  }`}
                >
                  {dragOverIndex === values.length && (
                    <span className="text-xs text-primary font-medium">Drop here</span>
                  )}
                </div>
              )}
            </div>
          );
          })}

        {/* Only show add button when field is editable */}
        {!isFieldReadOnly &&
          !isCollapsed &&
          field.multiple &&
          field.type !== "types" &&
          field.type !== "glossaryItem" &&
          field.type !== "media" && (
            <Button
              onClick={() => onAddFieldInstance(field.id)}
              variant="ghost"
              size="iconSmall"
              tooltip={`Add an extra field`}
            >
              <IconProvider iconstring="fa-plus" size={12} />
            </Button>
          )}
      </div>
    );
  };

  /**
   * Render single field
   */
  const renderSingleField = () => {
    if (field.type === "number") {
      const numericValue =
        typeof fieldValue === "number"
          ? fieldValue
          : fieldValue !== undefined && fieldValue !== null && fieldValue !== ""
          ? parseFloat(String(fieldValue)) || 0
          : 0;

      return (
        <div className="space-y-1.5">
          {renderFieldLabel(field)}
          
          {/* Field Documentation Display - When documentation mode is on */}
          {showDocumentation && field.documentation && (
            <div className="mb-2 p-2 bg-primary/10 border border-primary/30 rounded">
              <div 
                className="text-xs text-foreground prose prose-xs max-w-none"
                dangerouslySetInnerHTML={{ __html: field.documentation }}
              />
            </div>
          )}
          
          {isFieldReadOnly ? (
            <div
              className="p-2 border border-border rounded-sm bg-muted opacity-70"
              data-field-type={field.type}
            >
              {renderFieldValueForView(field, numericValue)}
            </div>
          ) : (
            getWizardElement(
              field,
              getFormattedFieldValue(field, numericValue),
              (value) => onLocalInputChange(field.id, value),
              (value) => onFieldBlur(field.id, value),
              gameId
            )
          )}
        </div>
      );
    }

    // Handle boolean fields - need to keep value as boolean, not convert to string
    if (field.type === "boolean") {
      const booleanValue = typeof fieldValue === "boolean" ? fieldValue : fieldValue === "true" || fieldValue === true;

      return (
        <div className="space-y-1.5">
          {renderFieldLabel(field)}
          
          {/* Field Documentation Display - When documentation mode is on */}
          {showDocumentation && field.documentation && (
            <div className="mb-2 p-2 bg-primary/10 border border-primary/30 rounded">
              <div 
                className="text-xs text-foreground prose prose-xs max-w-none"
                dangerouslySetInnerHTML={{ __html: field.documentation }}
              />
            </div>
          )}
          
          {isFieldReadOnly ? (
            <div
              className="p-2 border border-border rounded-sm bg-muted opacity-70"
              data-field-type={field.type}
            >
              {renderFieldValueForView(field, booleanValue)}
            </div>
          ) : (
            getWizardElement(
              field,
              booleanValue,
              (value) => onLocalInputChange(field.id, value),
              (value) => onFieldBlur(field.id, value),
              gameId
            )
          )}
        </div>
      );
    }

    // For other field types
    const stringValue =
      field.type === "url" &&
      fieldValue &&
      typeof fieldValue === "object" &&
      fieldValue.url !== undefined
        ? fieldValue.text || fieldValue.url || ""
        : typeof fieldValue === "string"
        ? fieldValue
        : fieldValue !== undefined && fieldValue !== null
        ? String(fieldValue)
        : "";

    return (
      <div className="space-y-1.5">
        {renderFieldLabel(field)}
        
        {/* Field Documentation Display - When documentation mode is on */}
        {showDocumentation && (
          <div className={`mb-2 p-2 rounded text-xs ${field.documentation ? 'bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-400 dark:border-blue-600' : 'bg-muted border-l-4 border-border'}`}>
            {field.documentation ? (
              <div 
                className="text-foreground prose prose-xs max-w-none"
                dangerouslySetInnerHTML={{ __html: field.documentation }}
              />
            ) : (
              <div className="text-muted-foreground italic">
                No documentation available
              </div>
            )}
          </div>
        )}
        
        {isFieldReadOnly ? (
          <div
            className="p-2 border border-border rounded-sm bg-muted opacity-70"
            data-field-type={field.type}
          >
            {renderFieldValueForView(field, fieldValue)}
          </div>
        ) : field.translation && field.string_id ? (
          <TranslatableFieldWrapper field={field} gameId={gameId}>
            {getWizardElement(
              field,
              getFormattedFieldValue(field, getCurrentFieldContent(field)),
              (value) => onLocalInputChange(field.id, value),
              (value) => onFieldBlur(field.id, value),
              gameId
            )}
          </TranslatableFieldWrapper>
        ) : (
          getWizardElement(
            field,
            getFormattedFieldValue(field, stringValue),
            (value) => onLocalInputChange(field.id, value),
            (value) => onFieldBlur(field.id, value),
            gameId
          )
        )}
      </div>
    );
  };

  // Special handling: types, glossaryItem, and dialog fields should always use single field rendering
  // even when multiple=true, because they manage their own array state internally
  const shouldRenderAsSingleField =
    !field.multiple ||
    field.type === "types" ||
    field.type === "glossaryItem" ||
    field.type === "glossary-item" ||
    field.type === "dialog";

  return shouldRenderAsSingleField
    ? renderSingleField()
    : renderMultipleField();
};
